import {
  ISupplierUserPermission,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {Controller, useForm} from 'react-hook-form';
import React, {useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  ESupplierUserPermissionIds,
  ESupplierUserPermissions,
} from 'constants/index';
import {
  ACCOUNTS_DEFAULT_VALUES,
  ADMIN_DEFAULT_VALUES,
  ADVISOR_DEFAULT_VALUES,
  SALESPERSON_DEFAULT_VALUES,
} from 'components/UserForm/UserPermissionsPresets';
import {PermissionsService} from 'services/PermissionsService';

export function UserPermissionsForm(props: {
  loggedInUser: ISupplierUserProfile;
  supplierPermissions: ISupplierUserPermission[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (permissionIds: number[]) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  smallWidth?: boolean;
  readonly?: boolean;
}) {
  type Role = 'Admin' | 'Salesperson' | 'Accounts' | 'Advisor' | 'Custom';
  const [role, setRole] = useState<Role>('Custom');

  type Submission = {[permissionId: string]: boolean};
  const supplierDefaultValues: Submission = {};
  Object.keys(ADVISOR_DEFAULT_VALUES).forEach(
    (permissionId) => (supplierDefaultValues[permissionId] = false), // set all as false initially so presets work
  );
  props.supplierPermissions.forEach(
    (permission) => (supplierDefaultValues[permission.id.toString()] = true),
  );

  // https://levelup.gitconnected.com/using-react-hook-form-with-material-ui-components-ba42ace9507a
  const {handleSubmit, control, setValue} = useForm<Submission>({
    defaultValues: {
      ...supplierDefaultValues,
    },
  });

  const onSubmit = (submission: Submission) => {
    const permissionIds = Object.keys(submission)
      .map((permissionId) => parseInt(permissionId))
      .filter((permissionId) => submission[permissionId]);
    props.onSubmit(permissionIds);
  };

  const handleRoleUpdate = (role: Role) => {
    setRole(role);
    const presetPermissions = getPresetPermissions(role);
    Object.keys(presetPermissions).forEach((permissionId: string) =>
      // @ts-ignore
      setValue(permissionId, presetPermissions[permissionId], {
        shouldTouch: true,
      }),
    );
  };

  const getPresetPermissions = (role: Role) => {
    switch (role) {
      case 'Admin':
        return ADMIN_DEFAULT_VALUES;
      case 'Salesperson':
        return SALESPERSON_DEFAULT_VALUES;
      case 'Accounts':
        return ACCOUNTS_DEFAULT_VALUES;
      case 'Advisor':
        return ADVISOR_DEFAULT_VALUES;
      case 'Custom':
        return {};
      default:
        throw Error('Unhandled role: ' + role);
    }
  };

  const allRoles: Role[] = [
    'Admin',
    'Salesperson',
    'Accounts',
    'Advisor',
    'Custom',
  ];

  const allSections = [
    companySection(props.loggedInUser),
    customerSection,
    productsSection,
    quotesSection,
    ordersSection,
  ];

  return (
    <div className="generic-form-container">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Box marginBottom={3}>
            <Box className={'row'}>
              {allRoles.map((roleType, index) => (
                <Button
                  key={index}
                  variant={role === roleType ? 'contained' : 'text'}
                  disabled={props.isLoading}
                  disableElevation
                  onClick={() => handleRoleUpdate(roleType)}
                  size={'small'}
                  className={'role-button'}
                  style={{fontWeight: 400, flex: 1}}
                >
                  {roleType}
                </Button>
              ))}
            </Box>
          </Box>
          {allSections.map((section, index) => (
            <Accordion key={index} defaultExpanded={true}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className={'accordion-title'} variant={'body1'}>
                  {section.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1} className="form-section">
                  {section.lines.map((permission) => (
                    <Grid
                      key={permission.id}
                      item
                      xs={6}
                      style={{marginTop: '-0.5rem'}}
                    >
                      <Controller
                        // @ts-ignore
                        name={permission.id}
                        control={control}
                        render={({field: {onChange, value}}) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="primary"
                                size={'small'}
                                defaultChecked={value}
                                checked={value}
                                onChange={(event) =>
                                  onChange(event.target.checked)
                                }
                                disabled={
                                  permission.disabled ||
                                  props.readonly ||
                                  props.isLoading
                                }
                              />
                            }
                            label={
                              <Typography variant={'caption'}>
                                {permission.description}
                              </Typography>
                            }
                          />
                        )}
                      />
                      <Divider />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className={'secondary-btn'}
                    variant="contained"
                    fullWidth
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                    disableElevation
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className={'primary-btn'}
                  type="submit"
                  variant="contained"
                  fullWidth
                  color="primary"
                  disabled={props.isLoading}
                  disableElevation
                >
                  {props.isLoading ? (
                    <CircularProgress
                      color={'primary'}
                      size={24}
                      thickness={6}
                    />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}

type PermissionSection = {
  title: string;
  lines: PermissionLine[];
};

type PermissionLine = {
  id: string;
  description: string;
  disabled?: boolean;
};

const companySection = (
  loggedInUser: ISupplierUserProfile,
): PermissionSection => {
  const permissions = new PermissionsService(loggedInUser);
  const canReadCompany = permissions.hasPermission(
    ESupplierUserPermissions.COMPANY_READ,
  );
  const canWriteCompany = permissions.hasPermission(
    ESupplierUserPermissions.COMPANY_WRITE,
  );
  const canReadDepot = permissions.hasPermission(
    ESupplierUserPermissions.DEPOT_READ,
  );
  const canWriteDepot = permissions.hasPermission(
    ESupplierUserPermissions.DEPOT_WRITE,
  );
  const canReadTeam = permissions.hasPermission(
    ESupplierUserPermissions.TEAM_READ,
  );
  const canWriteTeam = permissions.hasPermission(
    ESupplierUserPermissions.TEAM_WRITE,
  );
  return {
    title: 'Company',
    lines: [
      {
        id: ESupplierUserPermissionIds.COMPANY_READ.toString(),
        description: 'View Company Information',
        disabled: !canReadCompany,
      },
      {
        id: ESupplierUserPermissionIds.COMPANY_WRITE.toString(),
        description: 'Update Company Information',
        disabled: !canWriteCompany,
      },
      {
        id: ESupplierUserPermissionIds.DEPOT_READ.toString(),
        description: 'View Depot Information',
        disabled: !canReadDepot,
      },
      {
        id: ESupplierUserPermissionIds.DEPOT_WRITE.toString(),
        description: 'Update Depot Information',
        disabled: !canWriteDepot,
      },
      {
        id: ESupplierUserPermissionIds.TEAM_READ.toString(),
        description: 'View Team Member Information',
        disabled: !canReadTeam,
      },
      {
        id: ESupplierUserPermissionIds.TEAM_WRITE.toString(),
        description: 'Update Team Member Information',
        disabled: !canWriteTeam,
      },
    ],
  };
};

const customerSection: PermissionSection = {
  title: 'Customers',
  lines: [
    {
      id: ESupplierUserPermissionIds.CUSTOMER_READ.toString(),
      description: 'View Customer Information',
    },
    {
      id: ESupplierUserPermissionIds.CUSTOMER_CHAT_READ.toString(),
      description: 'View Customer Messages',
    },
    {
      id: ESupplierUserPermissionIds.CUSTOMER_CHAT_WRITE.toString(),
      description: 'Respond to Customer Messages',
    },
    {
      id: ESupplierUserPermissionIds.CUSTOMER_ADD.toString(),
      description: 'Add new customer',
    },
  ],
};

const productsSection: PermissionSection = {
  title: 'Products',
  lines: [
    {
      id: ESupplierUserPermissionIds.PRODUCT_READ.toString(),
      description: 'View Product Information',
    },
    {
      id: ESupplierUserPermissionIds.PRODUCT_WRITE.toString(),
      description: 'Update Product Information',
    },
    {
      id: ESupplierUserPermissionIds.DEPOT_PRODUCT_READ.toString(),
      description: 'View Product Depot Information',
    },
    {
      id: ESupplierUserPermissionIds.DEPOT_PRODUCT_WRITE.toString(),
      description: 'Update Product Depot Information',
    },
    {
      id: ESupplierUserPermissionIds.PRODUCT_PROMOTE.toString(),
      description: 'Promote Products on Inputs App',
    },
  ],
};

const quotesSection: PermissionSection = {
  title: 'Quotes',
  lines: [
    {
      id: ESupplierUserPermissionIds.QUOTE_READ.toString(),
      description: 'View Quotes',
    },
    {
      id: ESupplierUserPermissionIds.QUOTE_WRITE.toString(),
      description: 'Manage Quotes',
    },
    {
      id: ESupplierUserPermissionIds.QUOTE_CHAT_READ.toString(),
      description: 'View Quote Messages',
    },
    {
      id: ESupplierUserPermissionIds.QUOTE_CHAT_WRITE.toString(),
      description: 'Respond to Quote Messages',
    },
  ],
};

const ordersSection: PermissionSection = {
  title: 'Orders',
  lines: [
    {
      id: ESupplierUserPermissionIds.ORDER_READ.toString(),
      description: 'View Orders',
    },
    {
      id: ESupplierUserPermissionIds.ORDER_WRITE.toString(),
      description: 'Manage Orders',
    },
    {
      id: ESupplierUserPermissionIds.ORDER_CHAT_READ.toString(),
      description: 'View Order Messages',
    },
    {
      id: ESupplierUserPermissionIds.ORDER_CHAT_WRITE.toString(),
      description: 'Respond to Order Messages',
    },
  ],
};
